import classnames from 'classnames';
import _, { isArray, map } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import shallowEqual from 'shallowequal';
import Spinner from '../../../utils/Spinner';
import Empty from '../../../utils/Empty';
import TranslationContext from '../../../utils/Translations/TranslationContext';
import {
  nodePropType,
  selectionPropType,
  selectionVisualsPropType,
  treeAdapterPropTypes,
} from '../propTypes';
import { getNodeId } from '../store/actionHandlers/common/adapterActions';
import {
  initializeTree,
  resetTreeSelection,
  updateTree,
} from '../store/actions';
import {
  isTreeRootLoading,
  treeContext,
  treeFilter,
  treeInputModels,
  treeIsInitialized,
  treeSelection,
  treeSelectionVisuals,
} from '../store/selectors';

import withNextTreeStoreConnect from '../store/withNextTreeStoreConnect';
import Nodes from './Nodes';

import styles from './TreeBodyContent.scss';

@withNextTreeStoreConnect(
  (state, { tree, plugins }) => ({
    initialized: treeIsInitialized(tree)(state),
    selectionVisuals: treeSelectionVisuals(tree, plugins)(state),
    rootLoading: isTreeRootLoading(tree, plugins)(state),
    currentSelection: treeSelection(tree, plugins)(state),
    inputModels: treeInputModels(tree)(state),
    inputFilter: treeFilter(tree)(state),
    inputContext: treeContext(tree)(state),
    treeState: state[tree],
  }),
  (
    dispatch,
    { tree, models, adapter, plugins, filter, context, selection },
  ) => ({
    setUp: () =>
      dispatch(
        initializeTree(tree, {
          models,
          selection,
          adapter,
          plugins,
          filter,
          context,
        }),
      ),
    updateTree: () =>
      dispatch(updateTree(tree, models, filter, context, { adapter, plugins })),
    resetTreeSelection: selection =>
      dispatch(resetTreeSelection(tree, selection, { adapter, plugins })),
  }),
)
export default class TreeBodyContent extends React.PureComponent {
  static propTypes = {
    initialized: PropTypes.bool.isRequired,
    rootLoading: PropTypes.bool.isRequired,

    tree: PropTypes.string.isRequired,
    adapter: treeAdapterPropTypes.isRequired,
    plugins: PropTypes.object.isRequired,
    models: PropTypes.arrayOf(nodePropType),
    filter: PropTypes.any,
    context: PropTypes.any,

    selection: PropTypes.oneOfType([
      nodePropType,
      PropTypes.arrayOf(nodePropType),
    ]),

    onSelectionChange: PropTypes.func,
    resetTreeSelection: PropTypes.func.isRequired,

    selectionVisuals: selectionVisualsPropType,
    currentSelection: selectionPropType,
    setUp: PropTypes.func.isRequired,
    updateTree: PropTypes.func.isRequired,
    inputModels: PropTypes.array,
    inputFilter: PropTypes.any,
    inputContext: PropTypes.any,
    hasBottomPadding: PropTypes.bool,
    isScrollDisabled: PropTypes.bool,
  };

  static defaultProps = {
    models: null,
    filter: null,
    context: null,
    selection: null,
    selectionVisuals: null,
    onSelectionChange: null,
    currentSelection: null,
    inputModels: null,
    inputFilter: null,
    inputContext: null,
    hasBottomPadding: true,
    isScrollDisabled: false,
  };

  componentDidMount() {
    if (this.maybeInitialize()) {
      return;
    }

    this.maybeUpdateTree();

    const {
      selection,
      currentSelection,
      adapter,
      resetTreeSelection,
    } = this.props;

    if (!this.selectionEqual(selection, currentSelection, adapter)) {
      resetTreeSelection(selection);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.maybeInitialize()) {
      return;
    }

    this.maybeUpdateTree();

    const {
      selection,
      currentSelection,
      onSelectionChange,
      resetTreeSelection,
      adapter,
    } = this.props;

    const {
      currentSelection: prevCurrentSelection,
      selection: prevSelection,
    } = prevProps;

    const changedInTree =
      currentSelection !== prevCurrentSelection &&
      !this.selectionEqual(selection, currentSelection, adapter);

    const changedExternally =
      selection !== prevSelection &&
      !this.selectionEqual(selection, currentSelection, adapter);

    const changedToInitial =
      currentSelection !== prevCurrentSelection &&
      this.selectionEqual(selection, currentSelection, adapter);

    if (changedInTree && !changedExternally) {
      if (onSelectionChange) {
        onSelectionChange(currentSelection, this.getNodeMeta(currentSelection));
      }
    } else if (changedExternally) {
      resetTreeSelection(selection, this.getNodeMeta(selection));
    } else if (changedToInitial) {
      if (onSelectionChange) {
        onSelectionChange(currentSelection, this.getNodeMeta(selection));
      }
    }
  }

  static contextType = TranslationContext;

  getNodeMetaSingle = node => {
    const {
      adapter,
      treeState,
      plugins: { nodesMeta },
    } = this.props;

    return nodesMeta && node
      ? nodesMeta.getNodesMeta(treeState, getNodeId(adapter, node))
      : null;
  };

  getNodeMeta = selection =>
    isArray(selection)
      ? map(selection, this.getNodeMetaSingle)
      : this.getNodeMetaSingle(selection);

  maybeInitialize() {
    const { initialized, setUp } = this.props;
    if (!initialized) {
      setUp();
      return true;
    }
    return false;
  }

  maybeUpdateTree() {
    const {
      models,
      filter,
      context,
      inputModels,
      inputFilter,
      inputContext,
      updateTree,
    } = this.props;

    if (
      models !== inputModels ||
      filter !== inputFilter ||
      !shallowEqual(context, inputContext)
    ) {
      updateTree();
    }
  }

  selectionToIds(selection, adapter) {
    if (_.isEmpty(selection)) {
      return new Set();
    }

    if (Array.isArray(selection)) {
      return new Set(selection.map(node => getNodeId(adapter, node)));
    }

    return new Set([getNodeId(adapter, selection)]);
  }

  selectionEqual(left, right, adapter) {
    const leftIds = this.selectionToIds(left, adapter);
    const rightIds = this.selectionToIds(right, adapter);
    return _.isEqual(leftIds, rightIds);
  }

  // Check if all nodes are hidden by filters and canRender logic
  hasVisibleNodes() {
    const { treeState, plugins, adapter } = this.props;

    if (!treeState || !treeState.children) {
      return false;
    }

    const checkNodeVisibility = nodeId => {
      const model = treeState.models[nodeId];
      if (!model) {
        return false;
      }

      // Check filter visibility
      const { filter } = plugins;
      const visible = filter ? filter.nodeIsVisible(treeState, nodeId) : true;
      if (!visible) {
        return false;
      }

      // Check canRender logic
      if (adapter && adapter.canRender) {
        const { nodesMeta, dynamicLeaves } = plugins;
        const leaf =
          treeState.children[nodeId] === undefined ||
          !!(
            dynamicLeaves &&
            dynamicLeaves.shouldBeLeaf(treeState, nodeId, { plugins })
          );

        const nodeMeta = nodesMeta
          ? nodesMeta.getNodesMeta(treeState, nodeId)
          : {};
        const context = treeState.context;

        const isRenderable = adapter.canRender({
          id: nodeId,
          model,
          leaf,
          nodesMeta: nodeMeta,
          context,
          state: treeState,
        });

        if (!isRenderable) {
          return false;
        }
      }

      return true;
    };

    const checkChildrenRecursively = parentId => {
      const childrenIds = treeState.children[parentId] || [];

      for (const childId of childrenIds) {
        if (checkNodeVisibility(childId)) {
          return true;
        }

        // Check children recursively
        if (checkChildrenRecursively(childId)) {
          return true;
        }
      }

      return false;
    };

    // Check root level nodes
    return checkChildrenRecursively(null);
  }

  render() {
    const { direction, isLayoutRTL, t } = this.context;
    const {
      tree,
      initialized,
      rootLoading,
      selectionVisuals,
      plugins,
      adapter,
      hasBottomPadding,
      isScrollDisabled,
      handleClear,
    } = this.props;

    if (!initialized || rootLoading) {
      return <Spinner inline={false} />;
    }

    // Check if there are any visible nodes
    const hasVisibleNodes = this.hasVisibleNodes();

    if (!hasVisibleNodes) {
      return <Empty message={t('There is no data matching your criteria')} />;
    }

    const classNames = classnames(
      styles.tree,
      'jstree jstree-default jstree-default-large jstree-checkbox-selection',
      {
        'jstree-rtl': isLayoutRTL,
        [styles.radioTree]: selectionVisuals === 'radio',
        [styles.withBottomPadding]: hasBottomPadding,
        [styles.isScrollDisabled]: isScrollDisabled,
      },
    );

    return (
      <div
        className={classNames}
        data-layout-direction={direction}
        dir={direction}
      >
        <Nodes
          adapter={adapter}
          handleClear={handleClear}
          parentId={null}
          plugins={plugins}
          tree={tree}
        />
      </div>
    );
  }
};
